// The Calendar board's own content (kind "agenda": "calendar" is the content calendar's), the same in the browser,
// on the server and in the agent's tools: an event, and the board's own calendar settings; an event's times on
// anyone's clock; the days of a week and a month; where each event goes in a week's hours and on a month's days;
// and the quick add's words ("Lunch with Sam Friday 1pm 1h") as an event.
//
// A timed event keeps its times on its own time zone's clock, as Google Calendar does ("2026-10-30T09:00" in
// Europe/London), so a 9am meeting stays at 9am when the clocks change, and shows on each viewer's own clock. An
// all-day event is days, the same days everywhere, never moved by a time zone or the clocks.

import { DAY_WORDS, MAX_MINUTES, MONTH_WORDS, addDays, cleanDay, cleanZone, daysBetween, nextOn, parseQuick, timeLabel, wallClock, weekdayOf } from "./days";
import type { LayerMeta } from "./layers";
import type { Join, Reply } from "./meet";
import { RepeatRefused, cleanRepeat, pad, repeatDays, type RepeatRule } from "./repeat";
import { TEXT_COLORS, type TextColor } from "./textColors";
import type { Entry, ID } from "./types";
import { MONTHS } from "./calendar";

export interface EventData {
  title: string;
  /** all day: start and end are days; else times on the clock of tz */
  allDay: boolean;
  /** a timed event's start, "YYYY-MM-DDTHH:MM" on its zone's clock; an all-day event's first day, "YYYY-MM-DD" */
  start: string;
  /** a timed event's end, after its start; an all-day event's last day (itself included), on or after its first */
  end: string;
  /** the time zone a timed event's times are in, like Europe/London; null for an all-day event */
  tz: string | null;
  /** where it is: a place or an address, on one line; absent when none */
  location?: string;
  /** what the user wrote about it; absent when none */
  notes?: string;
  /** its own color, one of the board's named colors; absent: its calendar's */
  color?: TextColor;
  /** how it repeats (shared/repeat.ts); absent for once */
  repeat?: RepeatRule;
  /** its copies in Google calendars (Fixes 11): the calendar's key, and the id of this event's copy there */
  g?: Record<string, string>;
}

/** The board's own calendar settings: one entry per Calendar board, made when first changed. */
export interface CalSetData {
  /** the calendars and layers hidden on this board, by key ("board" is This board, "g:<account>:<calendar>" a Google one) */
  hidden: string[];
  /** calendars in this board's list that Google's own list leaves out (a holiday calendar the user unticked in Google): ticked here unless hidden */
  shown?: string[];
  /** calendars Google shows that this board's list leaves out (taken out on the account's page): this board only, the calendar stays connected */
  out?: string[];
  /** the Google calendars new events go into on this board, by key (Fixes 11); none: only on the board. Every event is on the board */
  into?: string[];
  /** before Fixes 11, the one calendar new events went to: read as the only one of into */
  default?: string;
}

/** The calendar every board event is in: the events that live on the board itself. */
export const BOARD_CAL = "board";
// "This board" (David, 1 Nov): "Only on this board" read like a filter, not the events the board keeps itself
export const BOARD_CAL_NAME = "This board";

/** The longest an event lasts: a year, so a typo never draws a decade. */
export const MAX_EVENT_DAYS = 366;
/** How long an event made with a click, or typed with a time and no length, lasts. */
export const DEFAULT_MINUTES = 60;
export const MAX_TITLE = 300;
export const MAX_LOCATION = 300;
export const MAX_NOTES = 8000;

// whole seconds are dropped; a time with a zone or an offset on it ("Z", "+01:00") is not a clock time, and is refused
const LOCAL_RE = /^(\d{4})-(\d{2})-(\d{2})[T ]([01]\d|2[0-3]):([0-5]\d)(?::00(?:\.0+)?)?$/;

export class AgendaRefused extends Error {}
const refuse = (message: string): never => {
  throw new AgendaRefused(message);
};

/** "YYYY-MM-DDTHH:MM", a real day and a clock time, or null. */
export function cleanLocal(x: unknown): string | null {
  if (typeof x !== "string") return null;
  const m = LOCAL_RE.exec(x.trim());
  if (!m || !cleanDay(`${m[1]}-${m[2]}-${m[3]}`)) return null;
  return `${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}`;
}

/** The day of a local time. */
export const dayPart = (local: string) => local.slice(0, 10);
/** Minutes after midnight of a local time. */
export const minPart = (local: string) => Number(local.slice(11, 13)) * 60 + Number(local.slice(14, 16));
/** A local time from a day and minutes after its midnight; past midnight runs into the next day. */
export function localOf(day: string, min: number): string {
  const m = Math.round(min);
  const days = Math.floor(m / MAX_MINUTES);
  const rest = m - days * MAX_MINUTES;
  return `${days ? addDays(day, days) : day}T${pad(Math.floor(rest / 60))}:${pad(rest % 60)}`;
}

/** Minutes from one local time to another, on the same clock. */
export function localMinutes(a: string, b: string): number {
  return daysBetween(dayPart(a), dayPart(b)) * MAX_MINUTES + minPart(b) - minPart(a);
}

/** A local time moved on by minutes, on the same clock. */
export const addLocal = (local: string, min: number) => localOf(dayPart(local), minPart(local) + min);

const asUtc = (local: string) => Date.UTC(+local.slice(0, 4), +local.slice(5, 7) - 1, +local.slice(8, 10), +local.slice(11, 13), +local.slice(14, 16));

/** A moment as a local time on a zone's clock (the runtime's own with no zone). */
export function localAt(t: number, tz?: string | null): string {
  const w = wallClock(t, tz);
  return localOf(w.day, w.min);
}

/** How far a zone's clock is ahead of UTC at a moment, in minutes. */
function offsetAt(t: number, tz: string | null): number {
  const whole = Math.floor(t / 60_000) * 60_000;
  return Math.round((asUtc(localAt(whole, tz)) - whole) / 60_000);
}

/**
 * The moment a time on a zone's clock happens (the runtime's own clock with no zone). A time the clocks skip (2:30
 * the night they go forward) is read as the hour after, as calendars do; a time that happens twice, as the first.
 */
export function momentOf(local: string, tz?: string | null): number {
  const u = asUtc(local);
  const zone = tz ?? null;
  const offs = [...new Set([offsetAt(u - 12 * 3_600_000, zone), offsetAt(u + 12 * 3_600_000, zone)])];
  const hits = offs.map((o) => u - o * 60_000).filter((t) => localAt(t, zone) === local);
  if (hits.length) return Math.min(...hits);
  return u - offsetAt(u - 12 * 3_600_000, zone) * 60_000;
}

/** A local time on one zone's clock, read on another's: the same moment. */
export function convertLocal(local: string, from: string | null, to: string | null): string {
  if (from === to) return local;
  return localAt(momentOf(local, from), to);
}

let here: string | null | undefined;
/** The time zone this runtime is in, when it can say: read once, as making a formatter is most of what it costs. */
export function hereZone(): string | null {
  if (here !== undefined) return here;
  try {
    here = cleanZone(Intl.DateTimeFormat().resolvedOptions().timeZone);
  } catch {
    here = null;
  }
  return here;
}

/** An event's fields: a title on one line, real times in order, a real zone, and words within their room. */
export function cleanEvent(raw: Record<string, unknown>, before: EventData | null): EventData {
  const title = "title" in raw ? (typeof raw.title === "string" ? raw.title.replace(/[\r\n\t]+/g, " ").trim().slice(0, MAX_TITLE) : "") : (before?.title ?? "");
  const allDay = typeof raw.allDay === "boolean" ? raw.allDay : (before?.allDay ?? false);
  // a change between all day and a time sends both its times, so an old time is never read as a day
  const sent = (k: "start" | "end") => (k in raw ? raw[k] : allDay === (before?.allDay ?? allDay) ? before?.[k] : undefined);
  let start: string;
  let end: string;
  let tz: string | null = null;
  if (allDay) {
    start = cleanDay(typeof sent("start") === "string" ? (sent("start") as string).slice(0, 10) : null) ?? refuse(`An all-day event starts on a day like 2026-10-30, not "${String(sent("start"))}".`);
    end = sent("end") === undefined || sent("end") === null ? start : (cleanDay(typeof sent("end") === "string" ? (sent("end") as string).slice(0, 10) : null) ?? refuse(`An all-day event's end is its last day, like 2026-10-31, not "${String(sent("end"))}".`));
    if (end < start) refuse("An all-day event's last day is on or after its first.");
    if (daysBetween(start, end) >= MAX_EVENT_DAYS) refuse(`An event lasts up to ${MAX_EVENT_DAYS} days.`);
  } else {
    start = cleanLocal(sent("start")) ?? refuse(`A timed event starts at a time like 2026-10-30T09:00, not "${String(sent("start"))}".`);
    end = cleanLocal(sent("end")) ?? refuse(`A timed event ends at a time like 2026-10-30T10:00, not "${String(sent("end"))}".`);
    if (end <= start) refuse("An event ends after it starts.");
    if (daysBetween(dayPart(start), dayPart(end)) > MAX_EVENT_DAYS) refuse(`An event lasts up to ${MAX_EVENT_DAYS} days.`);
    const z = "tz" in raw ? raw.tz : before?.tz;
    tz = cleanZone(z) ?? refuse(`A timed event names the time zone its times are in, like Europe/London${z ? `, not "${String(z)}"` : ""}.`);
  }
  const words = (k: "location" | "notes", max: number, oneLine: boolean): string | undefined => {
    const v = k in raw ? raw[k] : before?.[k];
    if (v === null || v === undefined) return undefined;
    if (typeof v !== "string") return refuse(`An event's ${k} is words, or null.`);
    const s = (oneLine ? v.replace(/[\r\n\t]+/g, " ") : v.replace(/\r\n?/g, "\n")).trim().slice(0, max);
    return s || undefined;
  };
  const location = words("location", MAX_LOCATION, true);
  const notes = words("notes", MAX_NOTES, false);
  const c = "color" in raw ? raw.color : before?.color;
  const color = c === null || c === undefined || c === "" ? undefined : (TEXT_COLORS as readonly string[]).includes(String(c)) ? (c as TextColor) : refuse(`An event's color is one of ${TEXT_COLORS.join(", ")}, or null for its calendar's.`);
  let repeat: RepeatRule | null;
  try {
    repeat = cleanRepeat("repeat" in raw ? raw.repeat : before?.repeat);
  } catch (e) {
    return refuse(e instanceof RepeatRefused ? e.message : "That repeat could not be read.");
  }
  // a repeat ends on or after its first day
  if (repeat?.until && repeat.until < start.slice(0, 10)) refuse("A repeat ends on or after the event's first day.");
  const g = cleanCopies("g" in raw ? raw.g : before?.g);
  return { title, allDay, start, end, tz, ...(location ? { location } : {}), ...(notes ? { notes } : {}), ...(color ? { color } : {}), ...(repeat ? { repeat } : {}), ...(g ? { g } : {}) };
}

/** An event's copies in Google calendars: a Google calendar's key and the copy's id there, a dozen at most; none when empty. */
export function cleanCopies(raw: unknown): Record<string, string> | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>).slice(0, 12)) if (k.startsWith("g:") && KEY_RE.test(k) && typeof v === "string" && v && v.length <= 1024) out[k] = v;
  return Object.keys(out).length ? out : undefined;
}

/** A calendar's key: printable, on one line, within its room (a Google calendar's id can hold # and @). */
const KEY_RE = /^[^\s\u0000-\u001f\u007f]{1,400}$/;

export function cleanCalSet(raw: Record<string, unknown>, before: CalSetData | null): CalSetData {
  const keys = (k: "hidden" | "shown" | "out") => {
    const list = k in raw ? raw[k] : before?.[k];
    return Array.isArray(list) ? [...new Set(list.filter((x): x is string => typeof x === "string" && KEY_RE.test(x)))].slice(0, 200) : [];
  };
  const shown = keys("shown");
  const out = keys("out");
  const into = intoOf({ ...before, ...raw } as Partial<CalSetData>);
  return { hidden: keys("hidden"), ...(shown.length ? { shown } : {}), ...(out.length ? { out } : {}), ...(into.length ? { into } : {}) };
}

/** The Google calendars new events go into: the list kept, else the one calendar an older board chose; never the board's own, which every event is in. */
export function intoOf(d: Partial<CalSetData> | undefined): string[] {
  const list = Array.isArray(d?.into) ? d.into : typeof d?.default === "string" ? [d.default] : [];
  return [...new Set(list.filter((x): x is string => typeof x === "string" && x !== BOARD_CAL && KEY_RE.test(x)))].slice(0, 12);
}

export const isEvent = (e: Pick<Entry, "t">) => e.t === "event";
export const eventOf = (e: Pick<Entry, "data">) => e.data as unknown as EventData;

/** The board's settings entry, if it has one: there is one per board, by this id, so two tabs never make two. */
export const calSetId = (boardId: ID) => `cs-${boardId}`.slice(0, 40);

export function calSetOf(entries: Entry[], boardId: ID): CalSetData {
  const d = entries.find((x) => x.t === "calset" && x.boardId === boardId)?.data as unknown as CalSetData | undefined;
  const into = intoOf(d);
  return { hidden: Array.isArray(d?.hidden) ? d.hidden : [], shown: Array.isArray(d?.shown) ? d.shown : [], out: Array.isArray(d?.out) ? d.out : [], ...(into.length ? { into } : {}) };
}

/** An event's title as people read it: an untitled one says so, as Google Calendar does. */
export function eventTitle(ev: Pick<EventData, "title">): string {
  return ev.title.trim() || "(No title)";
}

// ---------------------------------------------------------------------------
// an event on the viewer's clock

/** An event where the viewer is: its first and last day there, and a timed one's start and end on their clock. */
export interface Shown {
  /** its id on the board: an entry's, or for a Google event "<calendar key>#<Google's id>" */
  id: ID;
  ev: EventData;
  /** the calendar it is in: "board" for the board's own, "g:<account>:<calendar>" for a Google one */
  cal: string;
  /** its color as CSS, for a Google event ("#rrggbb"); null for the board's own, which use their named colors */
  color: string | null;
  /** a Google event's page in Google Calendar, and whether it is one of a repeat or not yet accepted */
  meta?: { mark2?: string; link?: string; recurring?: boolean; tentative?: boolean; join?: Join; guests?: number; you?: Reply; from?: string; layer?: LayerMeta };
  /** one time a repeating event happens: the series (the board's entry, or Google's repeating event) and its day there */
  series?: ID;
  occ?: string;
  allDay: boolean;
  /** a timed event's start and end on the viewer's clock, "YYYY-MM-DDTHH:MM"; an all-day one's days, "YYYY-MM-DD" */
  start: string;
  end: string;
  /** the first and last day it covers on the viewer's calendar (a timed event ending at midnight ends the day before) */
  first: string;
  last: string;
  /** a timed event that lasts a day or more is drawn with the all-day events, as calendars do */
  long: boolean;
}

/** A board event as the viewer sees it, on their clock (tz; the runtime's own with none). */
export function shownOf(e: Pick<Entry, "id" | "data">, tz?: string | null): Shown {
  return shownFrom(e.id, eventOf(e), tz, BOARD_CAL, null);
}

/** Any event (the board's own, or a Google calendar's) as the viewer sees it, on their clock. */
export function shownFrom(id: ID, ev: EventData, tz: string | null | undefined, cal: string, color: string | null): Shown {
  if (ev.allDay) return { id, ev, cal, color, allDay: true, start: ev.start, end: ev.end, first: ev.start, last: ev.end, long: true };
  const zone = tz ?? null;
  const start = ev.tz === zone || (!zone && ev.tz === hereZone()) ? ev.start : convertLocal(ev.start, ev.tz, zone);
  const end = ev.tz === zone || (!zone && ev.tz === hereZone()) ? ev.end : convertLocal(ev.end, ev.tz, zone);
  const first = dayPart(start);
  // an event that ends at midnight is over by the end of the day before
  const last = minPart(end) === 0 && dayPart(end) > first ? addDays(dayPart(end), -1) : dayPart(end);
  return { id, ev, cal, color, allDay: false, start, end, first, last, long: localMinutes(start, end) >= MAX_MINUTES };
}

/**
 * A board event's showings between two days (a to b, on the viewer's clock): itself, or each time it repeats there,
 * each with its own id ("<entry>@<day>"), the series it belongs to and its day on the event's own clock.
 */
export function showingsOf(e: Pick<Entry, "id" | "data">, tz: string | null | undefined, a: string, b: string): Shown[] {
  const ev = eventOf(e);
  if (!ev.repeat) return [shownOf(e, tz)];
  const first = ev.start.slice(0, 10);
  const spanDays = ev.allDay ? daysBetween(ev.start, ev.end) : daysBetween(dayPart(ev.start), dayPart(ev.end));
  const minutes = ev.allDay ? 0 : localMinutes(ev.start, ev.end);
  // a day either side for the zones, and the event's own length before a
  const days = repeatDays(ev.repeat, first, addDays(a, -(spanDays + 1)), addDays(b, 1));
  return days
    .map((d): Shown => {
      const start = ev.allDay ? d : `${d}${ev.start.slice(10)}`;
      const occ: EventData = { ...ev, start, end: ev.allDay ? addDays(d, spanDays) : addLocal(start, minutes) };
      return { ...shownFrom(`${e.id}@${d}`, occ, tz, BOARD_CAL, null), series: e.id, occ: d };
    })
    .filter((s) => touches(s, a, b));
}

/** Whether an event, as shown, touches the days from a to b (b included). */
export const touches = (s: Pick<Shown, "first" | "last">, a: string, b: string) => s.first <= b && s.last >= a;

/** The list's days (step 8): each day from `from` with what is on it; a run of days with nothing on (today aside) as one. */
export type ListRun = { day: string; list: Shown[] } | { free: [string, string] };

/** All day first, then by when it starts, then by its title. */
const byTime = (x: Shown, y: Shown) => (x.allDay !== y.allDay ? (x.allDay ? -1 : 1) : x.start.localeCompare(y.start) || eventTitle(x.ev).localeCompare(eventTitle(y.ev)));

export function listRuns(list: Shown[], from: string, today: string, n = LIST_DAYS): ListRun[] {
  const out: ListRun[] = [];
  for (let i = 0; i < n; i++) {
    const day = addDays(from, i);
    const on = list.filter((s) => touches(s, day, day)).sort(byTime);
    const last = out[out.length - 1];
    if (on.length || day === today) out.push({ day, list: on });
    else if (last && "free" in last) last.free[1] = day;
    else out.push({ free: [day, day] });
  }
  return out;
}

/** Working hours, for finding free time (step 9): minutes after midnight on the user's clock, and whether Saturday and Sunday count. */
export interface WorkHours {
  from: number;
  to: number;
  weekends: boolean;
}
export const WORK_HOURS: WorkHours = { from: 9 * 60, to: 18 * 60, weekends: false };

/**
 * The free times of at least `minutes` from one day to another (both included), inside working hours, around what is
 * on: each busy time a start and an end on the user's clock ("YYYY-MM-DDTHH:MM"). Before `now` (the user's clock, the
 * next quarter hour from it) nothing is free. Each day's free times in order, each as long as it is.
 */
export function freeSlots(busy: { start: string; end: string }[], from: string, to: string, minutes: number, hours: WorkHours = WORK_HOURS, now?: string): { day: string; start: string; end: string; minutes: number }[] {
  const out: { day: string; start: string; end: string; minutes: number }[] = [];
  const sorted = [...busy].filter((b) => b.end > b.start).sort((x, y) => x.start.localeCompare(y.start));
  const soon = now ? localOf(dayPart(now), Math.ceil(minPart(now) / 15) * 15) : null;
  for (let d = from; d <= to; d = addDays(d, 1)) {
    const wd = weekdayOf(d);
    if (!hours.weekends && (wd === 0 || wd === 6)) continue;
    const close = localOf(d, hours.to);
    let at = localOf(d, hours.from);
    if (soon && soon > at) at = soon;
    for (const b of sorted) {
      if (at >= close) break;
      if (b.end <= at) continue;
      if (b.start >= close) break;
      if (b.start > at && localMinutes(at, b.start) >= minutes) out.push({ day: d, start: at, end: b.start, minutes: localMinutes(at, b.start) });
      if (b.end > at) at = b.end;
    }
    if (at < close && localMinutes(at, close) >= minutes) out.push({ day: d, start: at, end: close, minutes: localMinutes(at, close) });
  }
  return out;
}

/** When an event is on one of its days, as the list says it: "09:00 to 10:00", "From 22:00", "Until 02:00", or All day. */
export function timeOnDay(s: Pick<Shown, "allDay" | "start" | "end" | "first" | "last">, day: string, h24: boolean): string {
  if (s.allDay) return "All day";
  const a = clockText(minPart(s.start), h24);
  const b = clockText(minPart(s.end), h24);
  if (s.first === day && s.last === day) return `${a} to ${b}`;
  if (s.first === day) return `From ${a}`;
  if (s.last === day) return `Until ${b}`;
  return "All day";
}

/** A timed event's part in one day's hours: minutes after that day's midnight, cut at its edges. */
export interface DayPart {
  shown: Shown;
  start: number;
  end: number;
  /** it began on a day before, or goes on after this one */
  cutStart: boolean;
  cutEnd: boolean;
}

/** The timed events (less than a day long) in one day's hours, on the viewer's clock. */
export function partsOn(list: Shown[], day: string): DayPart[] {
  const out: DayPart[] = [];
  for (const s of list) {
    if (s.allDay || s.long || !touches(s, day, day)) continue;
    const a = dayPart(s.start) < day ? 0 : minPart(s.start);
    const b = dayPart(s.end) > day ? MAX_MINUTES : minPart(s.end);
    out.push({ shown: s, start: a, end: Math.max(a, b), cutStart: dayPart(s.start) < day, cutEnd: dayPart(s.end) > day && !(dayPart(s.end) === addDays(day, 1) && minPart(s.end) === 0) });
  }
  return out.sort((x, y) => x.start - y.start || y.end - x.end);
}

/** How far a block starts after another it overlaps before it steps in over it rather than sitting beside it, in minutes. */
export const STEP_IN_AFTER = 30;
/** How far each step in is, as a share of the day's width. */
const STEP_IN = 0.16;

/**
 * Where each of a day's blocks sits across the day, as Google Calendar lays them (Fixes 9): blocks that start within half
 * an hour of each other sit side by side, sharing the width; one that starts later than that steps in over the ones
 * it overlaps, a little from their left, as wide as the rest of the day and drawn over them, so its title and the
 * earlier one's both show. left and width are shares of the day's width; level is how many steps in it is.
 */
export function overlapLayout<B extends { start: number; end: number }>(blocks: B[], min = 15): (B & { left: number; width: number; level: number })[] {
  const sorted = [...blocks].sort((a, b) => a.start - b.start || b.end - a.end);
  type Placed = B & { left: number; width: number; level: number; row: Placed[] };
  const placed: Placed[] = [];
  for (const b of sorted) {
    const end = Math.max(b.end, b.start + min);
    const over = placed.filter((p) => p.start < end && Math.max(p.end, p.start + min) > b.start);
    const near = over.find((p) => b.start - p.start < STEP_IN_AFTER);
    const me = { ...b, left: 0, width: 1, level: 0, row: [] as Placed[] } as Placed;
    if (near) {
      // beside the ones it started with: their row shares the room the first of them had
      const row = near.row;
      row.push(me);
      me.row = row;
      const base = row[0].left;
      const room = 1 - base;
      row.forEach((r, i) => Object.assign(r, { left: base + (room * i) / row.length, width: room / row.length, level: row[0].level }));
    } else if (over.length) {
      // later: a step in from the furthest in of the ones it overlaps, never past three quarters of the day
      const level = Math.max(...over.map((p) => p.level)) + 1;
      const left = Math.min(0.75, Math.max(...over.map((p) => p.left)) + STEP_IN);
      Object.assign(me, { left, width: 1 - left, level });
      me.row = [me];
    } else me.row = [me];
    placed.push(me);
  }
  return placed.map(({ row: _row, ...rest }) => rest as unknown as B & { left: number; width: number; level: number });
}

/** A bar across days in a row of days (a week's all-day band, a row of the month): its first and last column, its lane. */
export interface Bar {
  shown: Shown;
  from: number;
  to: number;
  lane: number;
  /** it began before the row, or goes on after it */
  cutStart: boolean;
  cutEnd: boolean;
}

/**
 * Lays bars across a row of days (from its first day, n days long): each event that touches the row gets the first
 * lane free over all its days, the earliest and then the longest first, as calendars stack them.
 */
export function barsIn(list: Shown[], first: string, n: number): Bar[] {
  const last = addDays(first, n - 1);
  const items = list
    .filter((s) => touches(s, first, last))
    .map((s) => ({ shown: s, from: Math.max(0, daysBetween(first, s.first)), to: Math.min(n - 1, daysBetween(first, s.last)), cutStart: s.first < first, cutEnd: s.last > last }))
    .sort((a, b) => a.from - b.from || b.to - b.from - (a.to - a.from) || (a.shown.allDay === b.shown.allDay ? a.shown.start.localeCompare(b.shown.start) : a.shown.allDay ? -1 : 1));
  const lanes: number[][] = [];
  const out: Bar[] = [];
  for (const it of items) {
    let lane = 0;
    while (lanes[lane]?.some((d) => d >= it.from && d <= it.to)) lane++;
    (lanes[lane] ??= []).push(...Array.from({ length: it.to - it.from + 1 }, (_, i) => it.from + i));
    out.push({ ...it, lane });
  }
  return out;
}

// ---------------------------------------------------------------------------
// days, weeks and months

/** The day a week starts on, 0 Sunday to 6 Saturday, from the locale (Monday in the UK, Sunday in the US). */
export function weekStartsOn(locale?: string): number {
  try {
    const loc = new Intl.Locale(locale ?? (typeof navigator !== "undefined" ? navigator.language : "en-GB")) as Intl.Locale & { weekInfo?: { firstDay: number }; getWeekInfo?: () => { firstDay: number } };
    const day = (loc.getWeekInfo?.() ?? loc.weekInfo)?.firstDay;
    if (day !== undefined) return day % 7;
  } catch {
    /* read from its region below */
  }
  // a browser that cannot say (Firefox): Sunday where most calendars start the week, else Monday
  return /^en$|-(US|CA|JP|IL|BR|MX|PH|KR|TW|HK|IN|ZA)$/i.test(locale ?? (typeof navigator !== "undefined" ? navigator.language : "")) ? 0 : 1;
}

/** The first day of the week a day is in. */
export function weekOf(day: string, startsOn: number): string {
  return addDays(day, -((weekdayOf(day) - startsOn + 7) % 7));
}

/** The days a month's grid shows: whole weeks from the one its first day is in to the one its last is in. */
export function monthDays(day: string, startsOn: number): string[] {
  const first = `${day.slice(0, 7)}-01`;
  const next = addMonthsTo(first, 1);
  const from = weekOf(first, startsOn);
  const to = addDays(weekOf(addDays(next, -1), startsOn), 6);
  const out: string[] = [];
  for (let d = from; d <= to; d = addDays(d, 1)) out.push(d);
  return out;
}

/** The same day n months on, kept within the month it lands in (Jan 31 and a month is Feb 28). */
export function addMonthsTo(day: string, n: number): string {
  const y = +day.slice(0, 4);
  const m = +day.slice(5, 7) - 1 + n;
  const yy = y + Math.floor(m / 12);
  const mm = ((m % 12) + 12) % 12;
  const dim = new Date(Date.UTC(yy, mm + 1, 0)).getUTCDate();
  return `${yy}-${pad(mm + 1)}-${pad(Math.min(+day.slice(8, 10), dim))}`;
}

export type AgendaView = "month" | "week" | "day" | "list";

/** How many days the list shows (step 8): four weeks from the day in view, a run with nothing on folded into one line. */
export const LIST_DAYS = 28;

/** The days a view shows, its first and last. */
export function viewSpan(view: AgendaView, at: string, startsOn: number): [string, string] {
  if (view === "day") return [at, at];
  if (view === "list") return [at, addDays(at, LIST_DAYS - 1)];
  if (view === "week") {
    const w = weekOf(at, startsOn);
    return [w, addDays(w, 6)];
  }
  const days = monthDays(at, startsOn);
  return [days[0], days[days.length - 1]];
}

/** The day one view on (or back): a day, a week or a month. */
export function stepView(view: AgendaView, at: string, n: number): string {
  if (view === "day") return addDays(at, n);
  if (view === "week") return addDays(at, 7 * n);
  if (view === "list") return addDays(at, LIST_DAYS * n);
  return addMonthsTo(`${at.slice(0, 7)}-01`, n);
}

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const dayN = (d: string) => Number(d.slice(8, 10));
const monN = (d: string) => Number(d.slice(5, 7)) - 1;

/**
 * What a view is showing, as its header says it: "October 2026", "Oct 26 to Nov 1, 2026" (a week, or the list's four), "Friday, October 30, 2026";
 * dmy, as the UK writes them: "26 Oct to 1 Nov 2026", "Friday 30 October 2026".
 */
export function viewTitle(view: AgendaView, at: string, startsOn: number, dmy = false): string {
  if (view === "month") return `${MONTHS[monN(at)]} ${at.slice(0, 4)}`;
  if (view === "day") return dmy ? `${WEEKDAYS[weekdayOf(at)]} ${dayN(at)} ${MONTHS[monN(at)]} ${at.slice(0, 4)}` : `${WEEKDAYS[weekdayOf(at)]}, ${MONTHS[monN(at)]} ${dayN(at)}, ${at.slice(0, 4)}`;
  const [a, b] = viewSpan(view === "list" ? "list" : "week", at, startsOn);
  if (dmy) {
    const short = (d: string) => `${dayN(d)} ${MONTHS[monN(d)].slice(0, 3)}`;
    if (a.slice(0, 4) !== b.slice(0, 4)) return `${short(a)} ${a.slice(0, 4)} to ${short(b)} ${b.slice(0, 4)}`;
    if (a.slice(0, 7) !== b.slice(0, 7)) return `${short(a)} to ${short(b)} ${b.slice(0, 4)}`;
    return `${dayN(a)} to ${short(b)} ${b.slice(0, 4)}`;
  }
  const short = (d: string) => `${MONTHS[monN(d)].slice(0, 3)} ${dayN(d)}`;
  if (a.slice(0, 4) !== b.slice(0, 4)) return `${short(a)}, ${a.slice(0, 4)} to ${short(b)}, ${b.slice(0, 4)}`;
  if (a.slice(0, 7) !== b.slice(0, 7)) return `${short(a)} to ${short(b)}, ${b.slice(0, 4)}`;
  return `${short(a)} to ${dayN(b)}, ${b.slice(0, 4)}`;
}

/** "Fri, Oct 30"; dmy, as the UK writes it: "Fri 30 Oct". */
export function dayLabel(day: string, dmy = false): string {
  return dmy ? `${WEEKDAYS[weekdayOf(day)].slice(0, 3)} ${dayN(day)} ${MONTHS[monN(day)].slice(0, 3)}` : `${WEEKDAYS[weekdayOf(day)].slice(0, 3)}, ${MONTHS[monN(day)].slice(0, 3)} ${dayN(day)}`;
}

/**
 * When an event is, in words on the viewer's clock: "9 AM to 10 AM", "Oct 30, 10 PM to Oct 31, 2 AM", "All day", "Oct
 * 30 to Nov 1"; dmy, as the UK writes it, on the 24-hour clock: "09:00 to 10:00", "Fri 30 Oct, 22:00 to Sat 31 Oct, 02:00".
 */
export function whenLabel(s: Shown, day?: string, dmy = false): string {
  if (s.allDay) return s.first === s.last ? "All day" : `${dayLabel(s.first, dmy)} to ${dayLabel(s.last, dmy)}`;
  const a = clockText(minPart(s.start), dmy);
  const b = clockText(minPart(s.end), dmy);
  if (dayPart(s.start) === dayPart(s.end) || s.first === s.last) return day && day !== s.first ? `${dayLabel(s.first, dmy)}, ${a} to ${b}` : `${a} to ${b}`;
  return `${dayLabel(dayPart(s.start), dmy)}, ${a} to ${dayLabel(dayPart(s.end), dmy)}, ${b}`;
}

/** A day as a date field shows it: 28/10/2026 day first, as the UK writes it, else 10/28/2026. */
export function dateText(day: string, dmy: boolean): string {
  return dmy ? `${day.slice(8, 10)}/${day.slice(5, 7)}/${day.slice(0, 4)}` : `${day.slice(5, 7)}/${day.slice(8, 10)}/${day.slice(0, 4)}`;
}

/** A zone's own short name at a moment ("BST", "GMT+1", "EDT"), for an event kept on another clock than the viewer's. */
export function zoneLabel(tz: string, t = Date.now()): string {
  try {
    const p = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "short" }).formatToParts(new Date(t)).find((x) => x.type === "timeZoneName");
    const city = tz.split("/").pop()?.replace(/_/g, " ") ?? tz;
    return p?.value ? `${city} (${p.value})` : city;
  } catch {
    return tz;
  }
}

// ---------------------------------------------------------------------------
// a time as the region writes it, and as people type it (Fixes 9)

/** A clock time as the region writes it: "14:30" and "09:00" on the 24-hour clock, else "2:30 PM" and "9 AM". */
export function clockText(min: number, h24: boolean): string {
  if (!h24) return timeLabel(min);
  const m = ((Math.round(min) % MAX_MINUTES) + MAX_MINUTES) % MAX_MINUTES;
  return `${pad(Math.floor(m / 60))}:${pad(m % 60)}`;
}

/**
 * A time as people type it in a time field: "14:30", "2.30pm", "2pm", "0230PM", "1430", "2:30 p.m.", "noon",
 * "midnight". With no AM or PM it is read on the 24-hour clock where the region tells the time so, and where an hour
 * is written with a leading zero or past 12; else an hour from 1 to 12 is the first of its morning or afternoon after
 * `after` (an end after its start), or with nothing to follow, the working day's (8 to 11 the morning, 12 to 7 after
 * noon), as the quick add reads "at 2". Minutes after midnight, or null for words that are no time.
 */
export function readClock(text: string, h24: boolean, after?: number): number | null {
  const t = text.trim().toLowerCase().replace(/\s+/g, "");
  if (!t) return null;
  if (/^(noon|midday|12noon)$/.test(t)) return 12 * 60;
  if (/^(midnight|12midnight)$/.test(t)) return 0;
  const m = /^(\d{1,2})(?:[:.h]?(\d{2}))?(am|pm|a\.m\.?|p\.m\.?|a|p)?$/.exec(t);
  if (!m) return null;
  const h = Number(m[1]);
  const mm = m[2] ? Number(m[2]) : 0;
  if (mm > 59) return null;
  if (m[3]) {
    if (h < 1 || h > 12) return h === 0 && m[3].startsWith("a") ? mm : null;
    return ((h % 12) + (m[3].startsWith("p") ? 12 : 0)) * 60 + mm;
  }
  if (h > 23) return null;
  if (h24 || h === 0 || h > 12 || m[1].startsWith("0")) return h * 60 + mm;
  const morning = (h % 12) * 60 + mm;
  const evening = morning + 12 * 60;
  if (after !== undefined) return morning > after ? morning : evening > after ? evening : morning;
  return h >= 8 && h <= 11 ? morning : h === 12 ? 12 * 60 + mm : evening;
}

// ---------------------------------------------------------------------------
// the quick add: "Lunch with Sam Friday 1pm 1h"

export interface QuickEvent {
  title: string;
  allDay: boolean;
  start: string;
  end: string;
  /** how it repeats, when the words said so ("every Monday", "every weekday", "daily") */
  repeat?: RepeatRule;
}

const WD_SRC = Object.keys(DAY_WORDS).sort((a, b) => b.length - a.length).join("|");
const MO_SRC = Object.keys(MONTH_WORDS).sort((a, b) => b.length - a.length).join("|");
/** A day in words: today, tomorrow, (this or next) a weekday, Oct 30, 30 Oct, 2026-10-30, 30/10 (as the region writes it). */
const DAY_SRC = `(?:today|tonight|tomorrow|tomorow|tmrw|tmr|(?:(?:next|this)\\s+)?(?:${WD_SRC})\\.?|(?:${MO_SRC})\\.?\\s+\\d{1,2}(?:st|nd|rd|th)?(?:,?\\s+\\d{4})?|\\d{1,2}(?:st|nd|rd|th)?\\s+(?:${MO_SRC})\\.?(?:,?\\s+\\d{4})?|\\d{4}-\\d{2}-\\d{2}|\\d{1,2}\\/\\d{1,2}(?:\\/\\d{2,4})?)`;
/** A time in words: 2pm, 2:30 pm, 14:30, noon. */
const TIME_SRC = `(?:noon|midday|midnight|\\d{1,2}(?:[:.]\\d{2})?\\s?(?:am|pm|a\\.m\\.|p\\.m\\.|a|p)?)`;
/** Between the two ends of a span: "to", "until", "through", a dash. */
const SPAN_SRC = `(?:\\s+(?:to|until|till|through|thru)\\s+|\\s*[-\u2013]\\s*)`;

/** The day a day in words names: a weekday is the next one from `from` (that day included), a date with no year the next to come. */
export function dayFrom(words: string, today: string, from: string, dmy: boolean): string | null {
  const w = words.trim().toLowerCase().replace(/\.$/, "");
  if (/^to(day|night)$/.test(w)) return today;
  if (/^(tomorrow|tomorow|tmrw|tmr)$/.test(w)) return addDays(today, 1);
  const wd = /^(?:(next|this)\s+)?([a-z]+)$/.exec(w);
  if (wd && DAY_WORDS[wd[2]] !== undefined) return nextOn(from, DAY_WORDS[wd[2]], wd[1] !== "next");
  const year = (y: string | undefined, mon: number, date: number) => {
    const at = (yy: number) => cleanDay(`${yy}-${pad(mon + 1)}-${pad(date)}`);
    if (y) return at(Number(y.length === 2 ? `20${y}` : y));
    const d = at(+from.slice(0, 4));
    return d && d < from ? at(+from.slice(0, 4) + 1) : d;
  };
  let m = /^([a-z]+)\.?\s+(\d{1,2})(?:st|nd|rd|th)?(?:,?\s+(\d{4}))?$/.exec(w);
  if (m && MONTH_WORDS[m[1]] !== undefined) return year(m[3], MONTH_WORDS[m[1]], +m[2]);
  m = /^(\d{1,2})(?:st|nd|rd|th)?\s+([a-z]+)\.?(?:,?\s+(\d{4}))?$/.exec(w);
  if (m && MONTH_WORDS[m[2]] !== undefined) return year(m[3], MONTH_WORDS[m[2]], +m[1]);
  if (/^\d{4}-\d{2}-\d{2}$/.test(w)) return cleanDay(w);
  m = /^(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?$/.exec(w);
  if (m) return year(m[3], (dmy ? +m[2] : +m[1]) - 1, dmy ? +m[1] : +m[2]);
  return null;
}

/** A span of times typed ("2pm to 4pm", "2-4pm", "14:00-15:30"): its start and length; an end with AM or PM lends it to a start without. */
function timeSpan(a: string, b: string, h24: boolean): { at: number; minutes: number } | null {
  const suffix = (t: string) => /[ap]\.?m?\.?$/i.test(t.trim()) || /noon|midday|midnight/i.test(t);
  // a bare "2-3" is no time, it might be anything: one end says it is a time
  if (!suffix(a) && !suffix(b) && !/[:.]/.test(a + b)) return null;
  const end = readClock(b, h24);
  if (end === null) return null;
  let start = readClock(a, h24, undefined);
  if (start === null) return null;
  if (!suffix(a) && suffix(b) && !/^0/.test(a.trim())) {
    // "2-4pm": the start takes the end's half of the day when it comes before it there
    const pm = /p/i.test(b);
    const h = (start % 720) + (pm ? 720 : 0);
    start = h < end ? h : start;
  }
  const minutes = end > start ? end - start : end + MAX_MINUTES - start;
  return minutes > 0 && minutes < MAX_MINUTES ? { at: start, minutes } : null;
}

/**
 * An event as people type it: "Lunch with Sam Friday 1pm 1h". Its day (a weekday, a date, today when a time is said
 * with none, else the day given), its time and length (an hour when none is said), or all day when no time is said or
 * "all day" is; a span of days ("Friday to Sunday", "30 Oct - 1 Nov") as one event across them, all day unless both
 * ends have a time; a span of times ("2-4pm"); and a repeat ("every Monday", "every weekday", "daily", "every 2
 * weeks", "monthly"). Every part it reads leaves the title (Fixes 9: "all day" and "Friday to Sunday" stayed in it). A
 * repeat word first in the words is the title's ("Weekly review"). dmy: 30/10 is the 30th of October, as the account's
 * region writes it; h24 the same for a bare hour.
 */
export function quickEvent(text: string, today: string, fallbackDay: string, opts: { dmy?: boolean; h24?: boolean } = {}): QuickEvent | null {
  const dmy = !!opts.dmy;
  const h24 = opts.h24 ?? dmy;
  // "2.30pm" and "at 14.30" are times, as a time field reads them: written with a colon for the readers below
  let s = ` ${text.replace(/\s+/g, " ")} `
    .replace(/(\d{1,2})\.([0-5]\d)(?=\s?(?:am|pm|a\.m\.|p\.m\.|a|p)(?=[\s.]))/gi, "$1:$2")
    .replace(/(\s(?:at|@)\s?)(\d{1,2})\.([0-5]\d)(?=\s)/gi, "$1$2:$3");
  const g = (src: string) => new RegExp(src, "gi");
  // each part found is cut out of the words; the last one said counts, as the quick add (parseQuick) reads them
  const take = (re: RegExp, f: (m: RegExpExecArray) => boolean) => {
    const hits: RegExpExecArray[] = [];
    for (let m = re.exec(s); m; m = re.exec(s)) hits.push(m);
    for (let i = hits.length - 1; i >= 0; i--) {
      const m = hits[i];
      if (!f(m)) continue;
      s = s.slice(0, m.index) + " " + s.slice(m.index + m[0].length);
      return true;
    }
    return false;
  };
  // all day, wherever and however often it is said
  let allDay = false;
  while (take(g(`\\s(?:all[\\s-]?day)(?=\\s|$)`), () => true)) allDay = true;
  // a repeat
  let repeat: RepeatRule | null = null;
  const daysIn = (list: string) => [...new Set(list.split(/\s*(?:,|\band\b|&)\s*/i).map((w) => DAY_WORDS[w.trim().toLowerCase().replace(/s$/, "")] ?? DAY_WORDS[w.trim().toLowerCase()]).filter((d) => d !== undefined))].sort((a, b) => a - b);
  take(g(`\\s(?:(?:every|each)\\s+(?:week\\s?day|work\\s?day|business\\s+day)s?|(?:on\\s+)?weekdays)(?=\\s)`), () => ((repeat = { freq: "weekly", days: [1, 2, 3, 4, 5] }), true)) ||
    take(g(`\\s(?:every|each)\\s+(?:(other|second)\\s+)?((?:${WD_SRC})s?(?:\\s*(?:,|and|&)\\s*(?:${WD_SRC})s?)*)(?=\\s)`), (m) => {
      const days = daysIn(m[2]);
      if (!days.length) return false;
      repeat = { freq: "weekly", days, ...(m[1] ? { every: 2 } : {}) };
      return true;
    }) ||
    take(g(`\\s(?:every|each)\\s+(?:(other|second)\\s+|(\\d{1,2})\\s+)?(day|week|fortnight|month|year)s?(?=\\s)`), (m) => {
      const n = m[1] ? 2 : m[2] ? Number(m[2]) : 1;
      if (n < 1 || n > 99) return false;
      const unit = m[3].toLowerCase();
      repeat = unit === "fortnight" ? { freq: "weekly", every: 2 * n } : { freq: ({ day: "daily", week: "weekly", month: "monthly", year: "yearly" } as const)[unit as "day"], ...(n > 1 ? { every: n } : {}) };
      return true;
    }) ||
    // a word for it, but not first: "Weekly review" is a title
    take(g(`\\s(daily|weekly|fortnightly|biweekly|monthly|yearly|annually)(?=\\s)`), (m) => {
      if (m.index === 0) return false;
      const w = m[1].toLowerCase();
      repeat = w === "daily" ? { freq: "daily" } : w === "weekly" ? { freq: "weekly" } : w === "fortnightly" || w === "biweekly" ? { freq: "weekly", every: 2 } : w === "monthly" ? { freq: "monthly" } : { freq: "yearly" };
      return true;
    }) ||
    // "standup Mondays 9am"
    take(g(`\\s(?:on\\s+)?(${WD_SRC})s(?=\\s)`), (m) => {
      const d = DAY_WORDS[m[1].toLowerCase()];
      if (d === undefined || m.index === 0) return false;
      repeat = { freq: "weekly", days: [d] };
      return true;
    });
  // a span of days, with a time at each end or none
  let first: string | null = null;
  let last: string | null = null;
  let span: { at: number; atEnd: number } | null = null;
  take(g(`\\s(?:from\\s+|on\\s+)?(${DAY_SRC})(?:\\s+(?:at\\s+)?(${TIME_SRC}))?${SPAN_SRC}(${DAY_SRC})(?:\\s+(?:at\\s+)?(${TIME_SRC}))?(?=\\s)`), (m) => {
    const a = dayFrom(m[1], today, today, dmy);
    const b = a && dayFrom(m[3], today, a, dmy);
    if (!a || !b || b < a || daysBetween(a, b) >= MAX_EVENT_DAYS) return false;
    const ta = m[2] ? readClock(m[2], h24) : null;
    const tb = m[4] ? readClock(m[4], h24) : null;
    // a lone time at one end is not the span's: put it back for the time below
    if ((m[2] && ta === null) || (m[4] && tb === null)) return false;
    if (ta !== null && tb !== null) {
      if (`${b}T${pad(Math.floor(tb / 60))}:${pad(tb % 60)}` <= `${a}T${pad(Math.floor(ta / 60))}:${pad(ta % 60)}`) return false;
      span = { at: ta, atEnd: tb };
    } else if (ta !== null || tb !== null) return false;
    first = a;
    last = b;
    return true;
  });
  // a span of times
  let times: { at: number; minutes: number } | null = null;
  if (!first)
    take(g(`\\s(?:from\\s+|at\\s+)?(${TIME_SRC})${SPAN_SRC}(${TIME_SRC})(?=\\s)`), (m) => {
      times = timeSpan(m[1], m[2], h24);
      return !!times;
    });
  // one day
  let day: string | null = first;
  if (!day)
    take(g(`\\s(?:on\\s+|by\\s+|due\\s+|from\\s+|starting\\s+)?(${DAY_SRC})(?=\\s)`), (m) => {
      day = dayFrom(m[1], today, today, dmy);
      return !!day;
    });
  // the time, the length and the title, as the quick add (parseQuick) reads them
  const q = parseQuick(s.trim(), today);
  const rule = repeat as RepeatRule | null;
  const timed = times as { at: number; minutes: number } | null;
  const both = span as { at: number; atEnd: number } | null;
  // the first time of a weekly repeat on chosen days: the next of them
  const repeatDay = rule?.freq === "weekly" && rule.days?.length ? rule.days.map((d) => nextOn(today, d, true)).sort()[0] : null;
  const on: string = day ?? repeatDay ?? q.day ?? (timed ? today : fallbackDay);
  const title = q.title
    .replace(/^(?:on|at|from|starting)\s+/i, "")
    .replace(/\s+(?:on|at|from|to|until|every|starting|-)$/i, "")
    .trim();
  if (!title && !day && !q.day && !q.at && !timed && !rule && !allDay) return null;
  const withRule = (e: Omit<QuickEvent, "repeat">): QuickEvent => (rule ? { ...e, repeat: cleanRepeat(rule) ?? undefined } : e);
  if (first && last) {
    if (both && !allDay) return withRule({ title, allDay: false, start: localOf(first, both.at), end: localOf(last, both.atEnd) });
    return withRule({ title, allDay: true, start: first, end: last });
  }
  if (allDay || (!q.at && !timed)) return withRule({ title, allDay: true, start: on, end: on });
  const at = timed ? localOf(on, timed.at) : `${on}T${q.at}`;
  return withRule({ title, allDay: false, start: at, end: addLocal(at, timed ? timed.minutes : (q.minutes ?? DEFAULT_MINUTES)) });
}

// ---------------------------------------------------------------------------
// what an agent reads

/**
 * The events on the days from a to b, on the viewer's clock, in order: all day first on each day, then by start. extra:
 * events already shown from elsewhere (a board's Google calendars), put among them.
 */
export function eventsBetween(entries: Entry[], a: string, b: string, tz?: string | null, extra: Shown[] = []): Shown[] {
  return [...entries.filter(isEvent).flatMap((e) => showingsOf(e, tz, a, b)), ...extra]
    .filter((s) => touches(s, a, b))
    .sort((x, y) => x.first.localeCompare(y.first) || (x.allDay === y.allDay ? x.start.localeCompare(y.start) : x.allDay ? -1 : 1));
}

/** A layer item's source (step 6), for an agent's structured copy: what it is, and its board and id there. */
export function layerData(s: Shown): { layer?: { kind: string; board: ID; entry: ID } } {
  const l = s.meta?.layer;
  return l ? { layer: { kind: l.kind, board: l.board, entry: l.entry } } : {};
}

/**
 * The week ahead as data, for read_board's structured copy: each event on the user's clock, with its own zone's times
 * and its calendar. extra: the board's Google events and its layers, shown.
 */
export function agendaData(entries: Entry[], boardId: ID, tz: string | null, now: number, extra: Shown[] = [], calName: (cal: string) => string | null = () => null): Record<string, unknown> {
  const today = wallClock(now, tz).day;
  const hidden = calSetOf(entries, boardId).hidden.includes(BOARD_CAL);
  // entries: the board's own events it shows (the caller leaves out those a hidden calendar hides: worker/mcp.ts shownOwn)
  const list = eventsBetween(entries, today, addDays(today, 6), tz, extra);
  return {
    timeZone: tz,
    today,
    boardEventsHidden: hidden,
    nextSevenDays: list.map((s) => ({ id: s.id, title: s.ev.title, allDay: s.allDay, start: s.start, end: s.end, keptIn: s.ev.tz, calendar: s.cal === BOARD_CAL ? BOARD_CAL_NAME : (calName(s.cal) ?? "Google Calendar"), ...(s.ev.location ? { location: s.ev.location } : {}), ...(s.ev.notes ? { notes: s.ev.notes.slice(0, 300) } : {}), ...layerData(s) })),
  };
}

/** How a line says a layer item (step 6): what it is on its own board, and the id it has there. */
const LAYER_WORDS = { post: ["a post on", "post id"], followup: ["a follow-up on", "deal id"] } as const;

/**
 * One event as a line says it: its title, when, where, which calendar (a Google one by name) and its id. A layer item
 * says the board it is on (calName names it) and its id there, where it is changed.
 */
export function eventLine(s: Shown, day: string | undefined, calName: (cal: string) => string | null = () => null, notes = false): string {
  const layer = s.meta?.layer;
  if (layer) {
    const [what, id] = LAYER_WORDS[layer.kind];
    // a post goes out at a moment: the half hour it is drawn as is no length of its own
    const when = layer.kind === "post" && !s.allDay ? `at ${timeLabel(minPart(s.start))}` : whenLabel(s, day);
    return `"${eventTitle(s.ev)}" (${when}, ${what} "${calName(s.cal) ?? "another board"}"${layer.sub ? `, ${layer.sub}` : ""}, ${id} ${layer.entry})`;
  }
  const cal = s.cal === BOARD_CAL ? "" : `, ${calName(s.cal) ?? "Google Calendar"}`;
  const words = notes && s.ev.notes ? `, notes: "${s.ev.notes.replace(/\s+/g, " ").slice(0, 400)}${s.ev.notes.length > 400 ? "…" : ""}"` : "";
  return `"${eventTitle(s.ev)}" (${whenLabel(s, day)}${s.ev.location ? `, at ${s.ev.location}` : ""}${cal}, id ${s.id}${words})`;
}

/** The days ahead in words, for read_board and read_calendar: each day with its events and their ids, on the user's clock. */
export function agendaLines(entries: Entry[], today: string, tz: string | null, days = 7, extra: Shown[] = [], calName: (cal: string) => string | null = () => null, from = today, notes = false): string[] {
  const last = addDays(from, days - 1);
  const list = eventsBetween(entries, from, last, tz, extra);
  const lines: string[] = [];
  for (let d = from, i = 0; i < days; d = addDays(d, 1), i++) {
    const on = list.filter((s) => touches(s, d, d));
    if (!on.length) continue;
    const n = daysBetween(today, d);
    lines.push(`- ${n === 0 ? "Today, " : n === 1 ? "Tomorrow, " : ""}${dayLabel(d)}: ${on.map((s) => eventLine(s, d, calName, notes)).join("; ")}`);
  }
  return lines;
}
